import type { AdamCommand, AdamCommandContext, AdamCommandResult, AdamSelectionState } from "./Command";

export type AdamHistoryState = {
  undoDepth: number;
  redoDepth: number;
};

const DEFAULT_MAX_DEPTH = 100;

/**
 * Undo/redo stacks with optional merge-on-push (typing coalesce).
 * Entries store commands + selection bookmarks — never full buffer snapshots.
 */
export class AdamCommandHistory {
  private readonly undoStack: HistoryEntry[] = [];
  private readonly redoStack: HistoryEntry[] = [];
  private readonly maxDepth: number;

  constructor(maxDepth = DEFAULT_MAX_DEPTH) {
    this.maxDepth = Math.max(1, maxDepth);
  }

  execute(
    command: AdamCommand,
    context: AdamCommandContext,
    options: {
      beforeSelection?: AdamSelectionState;
      afterSelection?: AdamSelectionState;
    } = {},
  ): AdamCommandResult {
    const previousSelection = cloneSelection(options.beforeSelection ?? context.selection);

    try {
      const result = command.execute(context);
      const entry: HistoryEntry = {
        command,
        beforeSelection: previousSelection,
        afterSelection: cloneSelection(options.afterSelection ?? context.selection),
      };
      context.selection = cloneSelection(entry.afterSelection);

      const previous = this.undoStack.at(-1);
      if (previous?.command.canMergeWith?.(command) && previous.command.merge) {
        this.undoStack[this.undoStack.length - 1] = {
          command: previous.command.merge(command),
          beforeSelection: previous.beforeSelection,
          afterSelection: entry.afterSelection,
        };
      } else {
        this.undoStack.push(entry);
        while (this.undoStack.length > this.maxDepth) {
          this.undoStack.shift();
        }
      }

      this.redoStack.length = 0;
      return result;
    } catch (error) {
      context.selection = previousSelection;
      throw error;
    }
  }

  undo(context: AdamCommandContext): AdamCommandResult | null {
    const entry = this.undoStack.pop();
    if (!entry) {
      return null;
    }
    const previousSelection = cloneSelection(context.selection);
    try {
      const result = entry.command.undo(context);
      context.selection = cloneSelection(entry.beforeSelection);
      this.redoStack.push(entry);
      return result;
    } catch (error) {
      context.selection = previousSelection;
      this.undoStack.push(entry);
      throw error;
    }
  }

  redo(context: AdamCommandContext): AdamCommandResult | null {
    const entry = this.redoStack.pop();
    if (!entry) {
      return null;
    }
    const previousSelection = cloneSelection(context.selection);
    try {
      const result = entry.command.redo(context);
      context.selection = cloneSelection(entry.afterSelection);
      this.undoStack.push(entry);
      while (this.undoStack.length > this.maxDepth) {
        this.undoStack.shift();
      }
      return result;
    } catch (error) {
      context.selection = previousSelection;
      this.redoStack.push(entry);
      throw error;
    }
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }

  /** Update the redo bookmark on the latest undo entry (post-execute cursor jump). */
  setLastAfterSelection(selection: AdamSelectionState | undefined): void {
    const entry = this.undoStack.at(-1);
    if (entry) {
      entry.afterSelection = cloneSelection(selection);
    }
  }

  getState(): AdamHistoryState {
    return {
      undoDepth: this.undoStack.length,
      redoDepth: this.redoStack.length,
    };
  }
}

type HistoryEntry = {
  command: AdamCommand;
  beforeSelection?: AdamSelectionState;
  afterSelection?: AdamSelectionState;
};

function cloneSelection(selection: AdamSelectionState | undefined): AdamSelectionState | undefined {
  if (!selection) {
    return undefined;
  }
  return {
    cursor: { ...selection.cursor },
    selAnchor: selection.selAnchor ? { ...selection.selAnchor } : undefined,
    selFocus: selection.selFocus ? { ...selection.selFocus } : undefined,
  };
}
